import { Injectable } from '@angular/core';
import { SessionService } from '@galaxy/core';
import { BasePersona, IAMApiService, IAMService, PersonaType, UserIAMApiService } from '@vendasta/iam';
import { parseTokenInsecure } from '@vendasta/iamv2';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';

export enum IsPartner {
  No,
  Yes,
  Unauthorized,
}

@Injectable()
export class UserService {
  private isPartner$$: BehaviorSubject<IsPartner> = new BehaviorSubject<IsPartner>(IsPartner.Unauthorized);
  isPartner$: Observable<IsPartner> = this.isPartner$$.asObservable();

  readonly isSuperAdmin$: Observable<boolean> = this.sessionService.getSessionId().pipe(
    switchMap((sessionId) => {
      if (!sessionId) {
        return of(false);
      }
      return this.userIamApi.listPersonas({ session: sessionId, type: 'superadmin' }).pipe(
        map((resp) => (resp?.personas?.length ?? 0) > 0),
        catchError(() => of(false)),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly impersonateeUserId$: Observable<string> = this.sessionService.getSessionId().pipe(
    map((session) => {
      if (!session) return '';
      try {
        return parseTokenInsecure(session)?.impersonateeUserId || '';
      } catch {
        return '';
      }
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isImpersonating$: Observable<boolean> = this.impersonateeUserId$.pipe(map((id) => !!id));

  private readonly partnerStaffOnSiteCache = new Map<string, Observable<boolean>>();

  constructor(
    private iamService: IAMService,
    private userIamApi: UserIAMApiService,
    private iamApi: IAMApiService,
    private sessionService: SessionService,
  ) {
    this.getLoggedInSubject(PersonaType.partner)
      .pipe(
        take(1),
        shareReplay({ bufferSize: 1, refCount: true }),
        map((result: BasePersona) => {
          if (result) {
            return IsPartner.Yes;
          }
          return IsPartner.No;
        }),
        catchError((err) => {
          if (!!err.status && err.status === 401) {
            return of(IsPartner.Unauthorized);
          }
          return of(IsPartner.No);
        }),
      )
      .subscribe((isPartner: IsPartner) => this.isPartner$$.next(isPartner));
  }

  hasPartnerStaffOnPartner$(partnerId$: Observable<string | null>): Observable<boolean> {
    return combineLatest([this.sessionService.getSessionId(), partnerId$]).pipe(
      switchMap(([sessionId, partnerId]) => {
        if (!sessionId || !partnerId) return of(false);
        const email = this.emailFromSession(sessionId);
        if (!email) return of(false);
        return this.listPartnerStaffByEmail(partnerId, email);
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  impersonateeHasPartnerStaffOnPartner$(partnerId$: Observable<string | null>): Observable<boolean> {
    return combineLatest([this.impersonateeUserId$, partnerId$]).pipe(
      switchMap(([impUserId, partnerId]) => {
        if (!impUserId || !partnerId) return of(false);
        return this.iamApi.getMultiUsers({ userIdentifiers: [{ userId: impUserId }] }).pipe(
          map((resp) => resp?.users?.[0]?.user?.email || ''),
          switchMap((email) => (email ? this.listPartnerStaffByEmail(partnerId, email) : of(false))),
          catchError(() => of(false)),
        );
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Impersonation-aware partner-staff gate scoped to the SITE partner. Superadmins pass on any site
   * unless impersonating (then only the impersonatee's site role counts). Emits false for each new
   * partner before its query resolves so a partner switch never shows the previous partner's verdict.
   */
  partnerStaffOnSite$(partnerId$: Observable<string | null>): Observable<boolean> {
    return partnerId$.pipe(
      distinctUntilChanged(),
      switchMap((partnerId) =>
        partnerId ? this.partnerStaffVerdict$(partnerId).pipe(startWith(false)) : of(false),
      ),
    );
  }

  /** Same verdict as partnerStaffOnSite$ but without the leading false sentinel — emits exactly one
   * resolved value per partner. Use for one-shot reads (on click) where skipping the sentinel by
   * counting emissions would hang on the single-emission empty-partner path. */
  partnerStaffOnSiteResolved$(partnerId$: Observable<string | null>): Observable<boolean> {
    return partnerId$.pipe(
      distinctUntilChanged(),
      switchMap((partnerId) => (partnerId ? this.partnerStaffVerdict$(partnerId) : of(false))),
    );
  }

  private partnerStaffVerdict$(partnerId: string): Observable<boolean> {
    return combineLatest([
      this.isSuperAdmin$.pipe(catchError(() => of(false))),
      this.isImpersonating$,
      this.memoizedPartnerStaffOnSite$(partnerId),
    ]).pipe(map(([isSuperAdmin, isImpersonating, isStaff]) => (!isImpersonating && isSuperAdmin) || isStaff));
  }

  private memoizedPartnerStaffOnSite$(partnerId: string): Observable<boolean> {
    const cached = this.partnerStaffOnSiteCache.get(partnerId);
    if (cached) {
      return cached;
    }
    const staff$ = this.isImpersonating$.pipe(
      switchMap((isImpersonating) =>
        isImpersonating
          ? this.impersonateeHasPartnerStaffOnPartner$(of(partnerId))
          : this.hasPartnerStaffOnPartner$(of(partnerId)),
      ),
      catchError(() => of(false)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.partnerStaffOnSiteCache.set(partnerId, staff$);
    return staff$;
  }

  private listPartnerStaffByEmail(partnerId: string, email: string): Observable<boolean> {
    return this.iamApi
      .listUsers({
        namespace: partnerId,
        email,
        pageSize: 1,
        userFilter: { subjectTypes: [PersonaType.partner], searchTerms: [] },
      })
      .pipe(
        map((resp) => (resp?.users?.length ?? 0) > 0),
        catchError(() => of(false)),
      );
  }

  private emailFromSession(session: string): string {
    try {
      const decoded = jwtDecode<{ sub?: string }>(session);
      return decoded?.sub || '';
    } catch {
      return '';
    }
  }

  private getLoggedInSubject(personaType: PersonaType, partnerId?: string): Observable<BasePersona | null> {
    return this.sessionService
      .getSessionId()
      .pipe(switchMap((sessionId) => this.iamService.getSubjectBySession(sessionId, personaType, partnerId)));
  }
}
